/**
 * extractAuthorBlocks
 *
 * Walks a ProseMirror/TipTap JSON document tree and groups text content by the
 * `author` mark attached to each text node.  Each top-level block (paragraph,
 * heading, listItem, blockquote, etc.) is treated as one "piece" of text per
 * author.
 *
 * Returns:
 *   { [uid]: string[] }   – e.g. { "abc123": ["Iran is...", "Trump is..."] }
 *
 * Only non-empty text pieces are included.  Text nodes without an author mark
 * are ignored (those are typically AI-generated or un-attributed content).
 */

const BLOCK_TYPES = new Set([
    'paragraph',
    'heading',
    'listItem',
    'blockquote',
    'codeBlock',
    'bulletList',
    'orderedList',
    'taskItem',
]);

/**
 * Collect all text nodes (with their marks) from a node tree into a flat array.
 * @param {object} node - ProseMirror node
 * @param {string[]} inheritedAuthorIds - author marks inherited from parent (none in standard PM, but guard anyway)
 * @returns {{ text: string, authorIds: string[] }[]}
 */
function collectTextSpans(node, inheritedAuthorIds = []) {
    if (!node) return [];

    if (node.type === 'text') {
        const authorIds = new Set(inheritedAuthorIds);
        if (Array.isArray(node.marks)) {
            for (const mark of node.marks) {
                if (mark.type === 'author' && mark.attrs?.userId) {
                    authorIds.add(mark.attrs.userId);
                }
            }
        }
        if (node.text && node.text.trim()) {
            return [{text: node.text, authorIds: Array.from(authorIds)}];
        }
        return [];
    }

    const spans = [];
    if (Array.isArray(node.content)) {
        for (const child of node.content) {
            spans.push(...collectTextSpans(child, inheritedAuthorIds));
        }
    }
    return spans;
}

/**
 * Collect author-attributed text from a single block node.
 * Returns a map of { uid -> text } for this block.
 */
function extractBlockByAuthor(blockNode) {
    const spans = collectTextSpans(blockNode);
    const byAuthor = {};

    for (const {text, authorIds} of spans) {
        for (const uid of authorIds) {
            byAuthor[uid] = (byAuthor[uid] || '') + text;
        }
    }

    // Trim each author's contribution
    for (const uid of Object.keys(byAuthor)) {
        byAuthor[uid] = byAuthor[uid].trim();
        if (!byAuthor[uid]) delete byAuthor[uid];
    }

    return byAuthor;
}

/**
 * Walk the top-level content of a document and collect text per author per block.
 * Recurses into list items and other container nodes.
 */
function walkNodes(nodes, result) {
    if (!Array.isArray(nodes)) return;

    for (const node of nodes) {
        if (!node) continue;

        if (BLOCK_TYPES.has(node.type)) {
            const blockByAuthor = extractBlockByAuthor(node);
            for (const [uid, text] of Object.entries(blockByAuthor)) {
                if (!result[uid]) result[uid] = [];
                result[uid].push(text);
            }

            // Also recurse into list items which are nested inside lists
            if ((node.type === 'bulletList' || node.type === 'orderedList') && Array.isArray(node.content)) {
                walkNodes(node.content, result);
            }
        } else if (Array.isArray(node.content)) {
            // Non-block container (e.g. doc root, custom wrappers)
            walkNodes(node.content, result);
        }
    }
}

/**
 * Main export: extract author blocks from a ProseMirror/TipTap JSON document.
 *
 * @param {object} prosemirrorJson - The full doc node (type: "doc", content: [...])
 * @returns {{ [uid: string]: string[] }} Map of uid to array of text snippets
 */
export function extractAuthorBlocks(prosemirrorJson) {
    const result = {};

    if (!prosemirrorJson) return result;

    const topLevel = prosemirrorJson.type === 'doc'
        ? (prosemirrorJson.content || [])
        : [prosemirrorJson];

    walkNodes(topLevel, result);

    return result;
}
